import { useDevices, useDaily } from '@daily-co/daily-react';
import { useCallback, useEffect, useRef } from 'react';
import { useDeviceStore } from './useDeviceStore';

type DeviceType = 'cam' | 'mic';
type DeviceState = { requested: boolean; enabled: boolean };

// Stores a local state for sync checks
type DeviceLocalStateRef = Record<DeviceType, DeviceState>;

export const useUserDevices = () => {
  const callObject = useDaily();

  const userEnabledDevices = useRef<DeviceLocalStateRef>({
    mic: { requested: false, enabled: false },
    cam: { requested: false, enabled: false },
  });

  const { enabledCam, enabledMic, setEnabledCam, setEnabledMic } = useDeviceStore();

  // note: current Cam and Mic are the browser provided access to.
  // It does not reflect the state of the toggle on the system check screen
  const { currentCam, currentMic, microphones, cameras, setMicrophone, setCamera, refreshDevices } =
    useDevices();

  const getPermissions = useCallback(
    async ({ video, audio }: { video?: boolean; audio?: boolean }) => {
      try {
        await navigator.mediaDevices.getUserMedia({ video, audio });
      } catch (err: unknown) {
        console.error('getUserMedia Error', err);
        throw err;
      }

      await refreshDevices();
    },
    [refreshDevices],
  );

  const updateDeviceState = useCallback(
    (device: DeviceType, update: Partial<DeviceState>) => {
      const currentState = userEnabledDevices.current;
      userEnabledDevices.current = {
        ...currentState,
        [device]: { ...currentState[device], ...update },
      };

      setEnabledCam(userEnabledDevices.current.cam.enabled ? currentCam : undefined);
      setEnabledMic(userEnabledDevices.current.mic.enabled ? currentMic : undefined);
    },
    [currentCam, currentMic, setEnabledCam, setEnabledMic],
  );

  useEffect(() => {
    if (currentMic?.device.deviceId && userEnabledDevices.current.mic.enabled) {
      setEnabledMic(currentMic);
    }
  }, [currentMic, setEnabledMic]);

  useEffect(() => {
    if (currentCam?.device.deviceId && userEnabledDevices.current.cam.enabled) {
      setEnabledCam(currentCam);
    }
  }, [currentCam, setEnabledCam]);

  const updateMicrophone = (deviceId: string) => {
    setMicrophone(deviceId).catch(console.error);
  };

  const updateCamera = (deviceId: string) => {
    setCamera(deviceId).catch(console.error);
  };

  const enableDevice = useCallback(
    async (deviceType: DeviceType, deviceId: string) => {
      if (!deviceId) {
        throw new Error('Unable to enable device: Missing device id.');
      }
      await callObject?.setInputDevicesAsync(
        deviceType === 'cam' ? { videoDeviceId: deviceId } : { audioDeviceId: deviceId },
      );
      if (deviceType === 'cam') {
        callObject?.setLocalVideo(true);
      } else {
        callObject?.setLocalAudio(true);
      }
      updateDeviceState(deviceType, { enabled: true });
    },
    [callObject, updateDeviceState],
  );

  const disableDevice = useCallback(
    async (deviceType: DeviceType) => {
      updateDeviceState(deviceType, { enabled: false, requested: false });

      await callObject?.setInputDevicesAsync(
        deviceType === 'mic'
          ? { audioSource: false, audioDeviceId: false }
          : { videoSource: false, videoDeviceId: false },
      );
      if (deviceType === 'mic') {
        callObject?.setLocalAudio(false, { forceDiscardTrack: true });
      } else {
        callObject?.setLocalVideo(false);
      }
    },
    [callObject, updateDeviceState],
  );

  const toggleDevice = useCallback(
    async (deviceType: DeviceType) => {
      const hasInfo =
        deviceType === 'cam' ? currentCam?.device.deviceId : currentMic?.device.deviceId;

      const isEnabled = hasInfo && userEnabledDevices.current[deviceType].enabled;

      updateDeviceState(deviceType, {
        requested: !(isEnabled || userEnabledDevices.current[deviceType].requested),
      });

      if (
        !userEnabledDevices.current[deviceType].enabled &&
        userEnabledDevices.current[deviceType].requested
      ) {
        try {
          await getPermissions(deviceType === 'cam' ? { video: true } : { audio: true });
        } catch (err) {
          // revert request
          updateDeviceState(deviceType, { requested: false });
          throw err;
        }
      } else {
        await disableDevice(deviceType);
      }
    },
    [
      currentCam?.device.deviceId,
      currentMic?.device.deviceId,
      disableDevice,
      getPermissions,
      updateDeviceState,
    ],
  );

  const toggleCamera = useCallback(async () => {
    return toggleDevice('cam');
  }, [toggleDevice]);

  const toggleMicrophone = useCallback(async () => {
    return toggleDevice('mic');
  }, [toggleDevice]);

  // We rely on the "microphones" and "cameras" lists that we get from useDevices
  // these two hooks will react to changes on those lists
  useEffect(() => {
    if (
      microphones?.[0] &&
      userEnabledDevices.current.mic.requested &&
      !userEnabledDevices.current.mic.enabled
    ) {
      enableDevice('mic', microphones[0].device.deviceId).catch(console.error);
    }
  }, [enableDevice, microphones]);

  useEffect(() => {
    if (
      cameras?.[0] &&
      userEnabledDevices.current.cam.requested &&
      !userEnabledDevices.current.cam.enabled
    ) {
      enableDevice('cam', cameras[0].device.deviceId).catch(console.error);
    }
  }, [enableDevice, cameras]);

  const stopUserDevices = async () => {
    await disableDevice('cam');
    await disableDevice('mic');
  };

  return {
    currentCam: enabledCam,
    currentMic: enabledMic,
    microphones,
    cameras,
    toggleCamera,
    updateCamera,
    stopUserDevices,
    updateMicrophone,
    toggleMicrophone,
    userEnabledDevices,
  };
};
